import { useToast } from "@/components/ui/use-toast";
import {
  Notification,
  useClearNotifications,
  useCreateNotification,
  useGetNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from "@/queries/notifications";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

interface NotificationsContextType {
  notifications: Notification[];
  isLoading: boolean;
  error: string | null;
  addNotification: (
    notification: Omit<Notification, "id" | "timestamp" | "is_read">
  ) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notification_id: number) => Promise<void>;
  clearNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationsProvider"
    );
  }
  return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  // Use the query hooks with the correct types
  const { data = [], isLoading, error: fetchError } = useGetNotifications();

  const createNotificationMutation = useCreateNotification();
  const markAllReadMutation = useMarkAllNotificationsRead();
  const markNotificationReadMutation = useMarkNotificationRead();
  const clearNotificationsMutation = useClearNotifications();

  // Manage error state across multiple mutations
  const [error, setError] = useState<string | null>(
    fetchError ? "Failed to load notifications" : null
  );

  // Update error state when fetch error changes
  useEffect(() => {
    if (fetchError) {
      setError("Failed to load notifications");
    }
  }, [fetchError]);

  const addNotification = async (
    notification: Omit<Notification, "id" | "timestamp" | "is_read">
  ): Promise<void> => {
    try {
      await createNotificationMutation.mutateAsync({
        play_id: notification.play_id,
        title: notification.title,
        message: notification.message,
      });

      // Check if it's a scheduled play notification
      const isScheduledPlay =
        notification.title === "Scheduled Play Starting Soon" &&
        notification.play_id;

      // Display toast for new notification with click action for scheduled plays
      toast({
        title: notification.title,
        description: notification.message,
        onClick: isScheduledPlay
          ? () => navigate(`/scheduled-plays?playId=${notification.play_id}`)
          : undefined,
      });
    } catch (err) {
      console.error("Error creating notification:", err);
      setError("Failed to create notification");
      throw err;
    }
  };

  const markAllAsRead = async (): Promise<void> => {
    try {
      await markAllReadMutation.mutateAsync();
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      setError("Failed to mark all notifications as read");
      throw err;
    }
  };

  const markAsRead = async (notification_id: number): Promise<void> => {
    try {
      await markNotificationReadMutation.mutateAsync(notification_id);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
      setError("Failed to mark all notifications as read");
      throw err;
    }
  };

  const clearNotifications = async (): Promise<void> => {
    try {
      await clearNotificationsMutation.mutateAsync();
    } catch (err) {
      console.error("Error clearing notifications:", err);
      setError("Failed to clear notifications");
      throw err;
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications: data as Notification[],
        isLoading,
        error,
        addNotification,
        markAllAsRead,
        markAsRead,
        clearNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
